import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Move, Crosshair, Loader2, X, ArrowUpRight, ChevronRight, Compass } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { AddressInput } from "@/components/common/AddressInput";
import { Field } from "@/components/common/Page";
import { get, toastError } from "@/lib/api";
import { Glass, Seg } from "./RigControls";
import { fmtLatLng, normBearing, bearingLabel, SITE_DETAIL } from "./siteGeo";

const fmt0 = (v) => Number(v).toLocaleString("nb-NO", { maximumFractionDigits: 0 });

/** Geocode a Google place (or free-text address) through the backend and hand lat/lng to the editor. */
export function useSiteGeocoder(ed) {
  const [busy, setBusy] = useState(false);
  const geocode = useCallback(async ({ place_id, address }) => {
    setBusy(true);
    try {
      const r = await get("/maps/geocode", place_id ? { place_id } : { address });
      if (r?.lat == null || r?.lng == null) throw new Error("Fant ingen koordinater for adressen");
      ed.placeSiteAt(r.lat, r.lng, r.formatted_address || address || null);
      return r;
    } catch (e) {
      toastError(e, "Kunne ikke slå opp adressen");
      return null;
    } finally {
      setBusy(false);
    }
  }, [ed]);
  return { geocode, busy };
}

/** Layer options for a Seg – unavailable providers stay visible but disabled, with the reason as a tooltip. */
export const layerOptions = (siteConfig) => (siteConfig?.layers || []).map((l) => ({ value: l.id, label: l.label, disabled: !l.available, title: l.available ? `${l.label} – ${l.attribution}` : `${l.label} er ikke konfigurert` }));

/* ---------------- floating: layer switcher + attribution (bottom-right of the canvas) ---------------- */
export function SiteLayerControl({ ed, className, style }) {
  const { site, siteConfig, siteLayer, setSite, canEdit } = ed;
  if (!siteConfig) return null;
  return (
    <div className={cn("flex flex-col items-end gap-1", className)} style={style} data-testid="rig-site-layer-control">
      <Glass className="p-0.5"><Seg size="sm" value={site?.layer || siteConfig.default_layer} options={layerOptions(siteConfig)} onChange={(layer) => setSite({ layer })} disabled={!canEdit} testId="rig-site-layer" /></Glass>
      <SiteAttribution layer={siteLayer} />
    </div>
  );
}

export const SiteAttribution = ({ layer, className }) => layer ? (
  <a href={layer.attribution_url} target="_blank" rel="noreferrer" className={cn("pointer-events-auto rounded bg-background/70 px-1.5 py-px text-[10px] leading-4 text-muted-foreground backdrop-blur-sm hover:text-foreground", className)} data-testid="rig-site-attribution">{layer.attribution}</a>
) : null;

/* ---------------- floating: "where is the site?" prompt when the project has no coordinates ---------------- */
export function SitePrompt({ ed, onClose }) {
  const { geocode, busy } = useSiteGeocoder(ed);
  const project = ed.project;
  return (
    <Glass className="w-[380px] max-w-[calc(100vw-1.5rem)] px-4 py-3" testId="rig-site-prompt">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium">Hvor står riggen?</div>
          <p className="mt-0.5 text-[12px] leading-4 text-muted-foreground">Prosjektet mangler koordinater. Søk opp tomten, så legges satellittbildet under riggen.</p>
        </div>
        <button type="button" onClick={onClose} className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Avbryt" data-testid="rig-site-prompt-close"><X className="h-3.5 w-3.5" /></button>
      </div>
      <div className="mt-2.5 flex items-center gap-2">
        <AddressInput className="flex-1" placeholder="Søk adresse eller sted…" testId="rig-site-prompt-address" disabled={busy} onSelect={async (s) => { const r = await geocode(s); if (r) onClose?.(r); }} />
        {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {project?.address && (
        <button type="button" disabled={busy} onClick={async () => { const r = await geocode({ address: project.address }); if (r) onClose?.(r); }} className="link mt-2 text-[12px]" data-testid="rig-site-prompt-use-project">Bruk prosjektadressen «{project.address}»</button>
      )}
    </Glass>
  );
}

/* ---------------- inspector section ---------------- */
const Row = ({ label, children, className }) => (
  <div className={cn("min-w-0", className)}>
    <div className="text-[11.5px] leading-4 text-muted-foreground">{label}</div>
    <div className="mt-0.5">{children}</div>
  </div>
);

function BearingControl({ value, onChange, disabled }) {
  const [text, setText] = useState(String(Math.round(value)));
  useEffect(() => setText(String(Math.round(value))), [value]);
  const commit = () => { const n = Number(String(text).replace(",", ".")); if (!Number.isNaN(n)) onChange(normBearing(n)); else setText(String(Math.round(value))); };
  return (
    <div data-testid="rig-site-bearing">
      <div className="flex items-center gap-2">
        <div className="relative w-[4.5rem] shrink-0">
          <Input type="number" min={0} max={359} step={1} value={text} disabled={disabled} onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} className="h-7 pr-5 font-mono text-[12.5px] tabular" data-testid="rig-site-bearing-input" aria-label="Rotasjon av tomt i grader" />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">°</span>
        </div>
        <Slider value={[normBearing(value)]} min={0} max={359} step={1} disabled={disabled} onValueChange={([v]) => onChange(v)} className="flex-1" aria-label="Rotasjon av tomt" data-testid="rig-site-bearing-slider" />
      </div>
      <div className="mt-1.5 flex items-center gap-1">
        {[-15, -1, 1, 15].map((d) => (
          <button key={d} type="button" disabled={disabled} onClick={() => onChange(normBearing(value + d))} className="inline-flex h-6 min-w-[2.1rem] items-center justify-center rounded bg-muted/60 px-1.5 font-mono text-[11px] tabular text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40" data-testid={`rig-site-bearing-step-${d > 0 ? "plus" : "minus"}${Math.abs(d)}`}>{d > 0 ? `+${d}` : d}°</button>
        ))}
        <button type="button" disabled={disabled || normBearing(value) === 0} onClick={() => onChange(0)} className="ml-auto inline-flex h-6 items-center gap-1 rounded px-1.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40" data-testid="rig-site-bearing-reset"><Compass className="h-3 w-3" /> Nord opp</button>
      </div>
    </div>
  );
}

export function SiteSection({ ed, Section }) {
  const { site, siteConfig, siteEnabled, siteLayer, setSite, enableSite, centerSiteUnderRig, clearSite, siteTool, setSiteTool, canEdit, mode, project, rigBounds, rigCenterLatLng } = ed;
  const editable = canEdit && mode === "design";
  const { geocode, busy } = useSiteGeocoder(ed);
  const [editAddress, setEditAddress] = useState(false);
  const hasSite = !!(site && site.lat != null);
  const bearing = site?.bearing_deg || 0;
  const rigHeading = normBearing(90 + bearing); // compass heading of the rig's +x (long) axis
  const w = rigBounds.x1 - rigBounds.x0, d = rigBounds.z1 - rigBounds.z0;
  const meta = !hasSite ? "ikke plassert" : !siteEnabled ? "skjult" : siteLayer?.label || "på";

  if (!siteConfig) {
    return (
      <Section title="Tomt" testId="rig-section-site" meta="…">
        <div className="text-[12px] leading-5 text-muted-foreground" data-testid="rig-site-unavailable">Kartlag er ikke tilgjengelig akkurat nå.</div>
      </Section>
    );
  }

  return (
    <Section title="Tomt" testId="rig-section-site" meta={meta} defaultOpen={hasSite}>
      {!hasSite ? (
        <div data-testid="rig-site-empty">
          <p className="text-[12px] leading-5 text-muted-foreground">Plasser riggen på en ekte tomt – satellittbilde eller kart under modulene, med sant nord og målestokk.</p>
          <div className="mt-2.5 space-y-2">
            {project?.lat != null && project?.lng != null && (
              <Button variant="outline" size="sm" disabled={!editable} onClick={() => enableSite(true)} data-testid="rig-site-use-project"><MapPin /> Vis prosjektets tomt</Button>
            )}
            <AddressInput placeholder="Søk adresse eller sted…" testId="rig-site-address" disabled={!editable || busy} onSelect={(s) => geocode(s)} />
            {project?.address && project?.lat == null && <button type="button" disabled={!editable || busy} onClick={() => geocode({ address: project.address })} className="link text-[12px]" data-testid="rig-site-geocode-project">Bruk prosjektadressen</button>}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* address */}
          <div data-testid="rig-site-address-row">
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12.5px]" title={site.address || undefined} data-testid="rig-site-address-text">{site.address || "Uten adresse"}</div>
                <div className="font-mono text-[11px] tabular text-muted-foreground" data-testid="rig-site-anchor-latlng">{fmtLatLng(site.lat, site.lng)}</div>
              </div>
              {editable && <button type="button" onClick={() => setEditAddress((v) => !v)} className="shrink-0 text-[11.5px] text-muted-foreground hover:text-foreground" data-testid="rig-site-address-edit">{editAddress ? "Lukk" : "Endre"}</button>}
            </div>
            {editAddress && editable && (
              <div className="mt-2 flex items-center gap-2">
                <AddressInput className="flex-1" placeholder="Søk ny adresse…" testId="rig-site-address" disabled={busy} onSelect={async (s) => { const r = await geocode(s); if (r) setEditAddress(false); }} />
                {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            )}
            {!siteEnabled && <button type="button" onClick={() => enableSite(true)} className="link mt-1.5 text-[12px]" data-testid="rig-site-show">Vis tomten under riggen</button>}
          </div>

          <Row label="Kartlag">
            <Seg size="sm" className="bg-muted/60" value={site.layer || siteConfig.default_layer} options={layerOptions(siteConfig)} onChange={(layer) => setSite({ layer })} disabled={!editable} testId="rig-site-layer-inspector" />
          </Row>

          <Row label={<span className="inline-flex items-baseline gap-1.5">Rotasjon av tomt <span className="font-mono tabular text-foreground/80" data-testid="rig-site-heading">· rigg peker {bearingLabel(rigHeading)} {fmt0(rigHeading)}°</span></span>}>
            <BearingControl value={bearing} onChange={(v) => setSite({ bearing_deg: v })} disabled={!editable} />
          </Row>

          <Row label="Plassering">
            <div className="flex flex-wrap items-center gap-1.5">
              <Button variant={siteTool === "move" ? "default" : "outline"} size="sm" disabled={!editable || !siteEnabled} onClick={() => setSiteTool((t) => (t === "move" ? null : "move"))} aria-pressed={siteTool === "move"} title="Dra i kartet for å skyve tomten under riggen (M)" data-testid="rig-site-move"><Move /> Flytt tomt</Button>
              <Button variant="outline" size="sm" disabled={!editable} onClick={centerSiteUnderRig} title="Legg koordinatet i midten av riggen" data-testid="rig-site-center"><Crosshair /> Sentrer</Button>
            </div>
            <p className="mt-1.5 text-[11px] leading-4 text-muted-foreground">{siteTool === "move" ? "Dra i kartet. Riggen står stille – tomten glir under. Esc avslutter." : "Riggen beholder sine koordinater; det er kartet som flyttes og roteres under den."}</p>
          </Row>

          <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5">
            <Field label="Riggens senter" mono className="col-span-2"><span data-testid="rig-site-center-latlng">{rigCenterLatLng ? fmtLatLng(rigCenterLatLng.lat, rigCenterLatLng.lng) : "–"}</span></Field>
            <Field label="Fotavtrykk" value={`${fmt0(w)} × ${fmt0(d)} m`} mono />
            <Field label="Areal (bbox)" value={`${fmt0(w * d)} m²`} mono />
          </dl>
          {rigCenterLatLng && <a href={`https://www.google.com/maps?q=${rigCenterLatLng.lat.toFixed(6)},${rigCenterLatLng.lng.toFixed(6)}`} target="_blank" rel="noreferrer" className="link inline-flex items-center gap-1 text-[12px]" data-testid="rig-site-open-maps">Åpne i Google Maps <ArrowUpRight className="h-3 w-3" /></a>}

          <Collapsible>
            <CollapsibleTrigger className="group inline-flex h-6 items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground" data-testid="rig-site-advanced">
              <ChevronRight className="h-3 w-3 transition-transform duration-150 group-data-[state=open]:rotate-90" /> Avansert
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-3">
                <Row label="Detaljnivå på kart">
                  <Seg size="sm" className="bg-muted/60" value={site.zoom || siteConfig.default_zoom} options={SITE_DETAIL.filter((o) => o.value <= (siteLayer?.max_zoom ?? 19)).map((o) => ({ value: o.value, label: o.label, title: o.hint }))} onChange={(zoom) => setSite({ zoom })} disabled={!editable} testId="rig-site-zoom" />
                  <p className="mt-1 text-[11px] leading-4 text-muted-foreground">Høyere detalj gir skarpere bilde nær riggen, men dekker mindre område.</p>
                </Row>
                {editable && <button type="button" onClick={() => { clearSite(); toast("Tomteplasseringen er fjernet", { description: "Riggen vises igjen på rutenett." }); }} className="text-[12px] text-destructive hover:underline" data-testid="rig-site-clear">Fjern tomteplassering</button>}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </Section>
  );
}

/** Small icon+label used in the Grid | Tomt toggle. */
export const SegIconLabel = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" strokeWidth={1.75} />{children}</span>
);
